import type { ExecutionCandle, ExecutionCandlesRegimeOverlay } from "../../../../../types/trade";
import { executionCandleRowTime } from "@/utils/executionCandlesNormalize";
import { regimeOverlayFillRgba } from "../../../../../utils/marketConditionRegimeColors";

export type PreparedRegimeCandle = {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  color?: string;
  borderColor?: string;
  wickColor?: string;
  /** Normalized market_condition for this bar (after zip/dedupe). */
  regimeCondition: string;
};

export type PrepareExecutionCandlesWithRegimeInput = {
  candles: ExecutionCandle[] | null | undefined;
  regimeOverlay: ExecutionCandlesRegimeOverlay | null | undefined;
  focusTradeId?: string | null;
  focusDimmedBody?: string;
  focusDimmedWick?: string;
};

/**
 * Zip `candles[i]` with `regime_overlay.conditions[i]`, skip invalid bars together,
 * dedupe by time (keep last condition). Safe when lengths diverge after filtering.
 */
export function prepareExecutionCandlesWithRegime(
  input: PrepareExecutionCandlesWithRegimeInput
): PreparedRegimeCandle[] {
  const candles = input.candles || [];
  const conditions = input.regimeOverlay?.conditions ?? [];
  const dimUnrelated = Boolean(input.focusTradeId);
  const list: PreparedRegimeCandle[] = [];

  for (let i = 0; i < candles.length; i += 1) {
    const c = candles[i];
    const timeRaw = executionCandleRowTime(c);
    if (!timeRaw || c.open == null || c.high == null || c.low == null || c.close == null) {
      continue;
    }
    const ts = toUtcTimestampSecondsLocal(timeRaw);
    if (!ts) continue;
    const dimmed = dimUnrelated && c.related_to_focus_trade === false;
    const conditionRaw = conditions[i];
    const point: PreparedRegimeCandle = {
      time: ts,
      open: Number(c.open),
      high: Number(c.high),
      low: Number(c.low),
      close: Number(c.close),
      regimeCondition:
        typeof conditionRaw === "string" && conditionRaw.trim()
          ? conditionRaw.trim().toLowerCase()
          : "unknown",
    };
    if (dimmed && input.focusDimmedBody) {
      point.color = input.focusDimmedBody;
      point.borderColor = input.focusDimmedBody;
      point.wickColor = input.focusDimmedWick ?? input.focusDimmedBody;
    }
    list.push(point);
  }

  list.sort((a, b) => a.time - b.time);
  const deduped: PreparedRegimeCandle[] = [];
  for (const point of list) {
    const prev = deduped[deduped.length - 1];
    if (prev && prev.time === point.time) {
      prev.high = Math.max(prev.high, point.high);
      prev.low = Math.min(prev.low, point.low);
      prev.close = point.close;
      prev.regimeCondition = point.regimeCondition;
      if (point.color) {
        prev.color = point.color;
        prev.borderColor = point.borderColor;
        prev.wickColor = point.wickColor;
      }
    } else {
      deduped.push({ ...point });
    }
  }
  return deduped;
}

export function buildRegimeHistogramData(
  prepared: readonly PreparedRegimeCandle[]
): Array<{ time: number; value: number; color: string }> {
  return prepared.map((c) => ({
    time: c.time,
    value: 1,
    color: regimeOverlayFillRgba(c.regimeCondition),
  }));
}

function toUtcTimestampSecondsLocal(value: string | number): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
  }
  const s = String(value ?? "").trim();
  if (!s) return null;
  const ms = Date.parse(s);
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000);
}
